import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../shared/utils/pdfTurkishFont';
import { formatFinancialStatementAmount } from './financialPositionPresentation';

const EXCEL_COLORS = {
  title: 'FF16324F',
  section: 'FFE9EEF5',
  group: 'FFF7F9FC',
  summary: 'FFF2F6FB',
  strong: 'FFE1EAF5',
  total: 'FFDCE6F2',
  border: 'FFD3DAE5',
  text: 'FF132238'
};

const PDF_COLORS = {
  title: [22, 50, 79],
  section: [233, 238, 245],
  group: [247, 249, 252],
  summary: [242, 246, 251],
  strong: [225, 234, 245],
  total: [220, 230, 242],
  text: [19, 34, 56],
  border: [211, 218, 229]
};

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function buildFlatRows(presentation) {
  const rows = [];
  presentation.sections.forEach((section) => {
    rows.push({ type: 'section', label: section.title, noteRef: '', current: '', previous: '' });
    section.groups.forEach((group) => {
      rows.push({
        type: group.variant === 'summary-strong' ? 'summary-strong' : group.variant === 'summary' ? 'summary' : 'group',
        label: group.title,
        noteRef: '',
        current: group.currentTotal,
        previous: group.previousTotal
      });
      group.rows.forEach((row) => {
        rows.push({
          type: 'line',
          label: row.name,
          noteRef: row.noteRef || '',
          current: row.currentAmount,
          previous: row.previousAmount
        });
      });
    });
    rows.push({
      type: 'total',
      label: section.totalLabel,
      noteRef: '',
      current: section.currentTotal,
      previous: section.previousTotal
    });
  });

  (presentation.footerRows || []).forEach((row) => {
    rows.push({
      type: row.variant || 'total',
      label: row.label,
      noteRef: '',
      current: row.current,
      previous: row.previous
    });
  });

  return rows;
}

function rowStyleForExcel(type) {
  switch (type) {
    case 'section':
      return { fill: EXCEL_COLORS.section, bold: true };
    case 'group':
      return { fill: EXCEL_COLORS.group, bold: true };
    case 'summary':
      return { fill: EXCEL_COLORS.summary, bold: true };
    case 'summary-strong':
      return { fill: EXCEL_COLORS.strong, bold: true };
    case 'total':
      return { fill: EXCEL_COLORS.total, bold: true };
    default:
      return { fill: null, bold: false };
  }
}

export async function exportCashFlowToExcel({ presentation, filename, companyName }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('IFRS_Cash_Flow', {
    views: [{ state: 'frozen', ySplit: 4 }]
  });

  sheet.columns = [
    { width: 42 },
    { width: 12 },
    { width: 18 },
    { width: 18 }
  ];

  sheet.mergeCells('A1:D1');
  sheet.getCell('A1').value = presentation.labels.statementName;
  sheet.getCell('A1').font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.title } };
  sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.mergeCells('A2:D2');
  sheet.getCell('A2').value = companyName || '';
  sheet.getCell('A2').font = { size: 10, italic: true, color: { argb: EXCEL_COLORS.text } };
  sheet.getCell('A2').alignment = { horizontal: 'center' };

  sheet.addRow([
    presentation.labels.lineItem,
    presentation.labels.noteRef,
    presentation.currentPeriodLabel,
    presentation.previousPeriodLabel
  ]);

  const headerRow = sheet.getRow(3);
  for (let columnIndex = 1; columnIndex <= 4; columnIndex += 1) {
    const cell = headerRow.getCell(columnIndex);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.title } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  }

  buildFlatRows(presentation).forEach((item) => {
    const row = sheet.addRow([item.label, item.noteRef || '', item.current, item.previous]);
    const style = rowStyleForExcel(item.type);
    row.font = { bold: style.bold, color: { argb: EXCEL_COLORS.text } };
    if (style.fill) {
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: style.fill } };
      });
    }
    row.eachCell((cell, index) => {
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        left: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        bottom: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'thin', color: { argb: EXCEL_COLORS.border } }
      };
      cell.alignment = { vertical: 'middle', horizontal: index >= 3 ? 'right' : 'left' };
      if (index >= 3) {
        cell.numFmt = '#,##0.00';
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
}

export async function exportCashFlowToPdf({ presentation, filename, companyName }) {
  const rows = buildFlatRows(presentation);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  doc.setFontSize(15);
  doc.setTextColor(...PDF_COLORS.title);
  doc.text(presentation.labels.statementName, 148.5, 14, { align: 'center' });

  if (companyName) {
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.text);
    doc.text(companyName, 148.5, 20, { align: 'center' });
  }

  autoTable(doc, {
    startY: companyName ? 26 : 20,
    head: [[
      presentation.labels.lineItem,
      presentation.labels.noteRef,
      presentation.currentPeriodLabel,
      presentation.previousPeriodLabel
    ]],
    body: rows.map((row) => ([
      row.label,
      row.noteRef || '',
      typeof row.current === 'number' ? formatFinancialStatementAmount(row.current, presentation.locale) : '',
      typeof row.previous === 'number' ? formatFinancialStatementAmount(row.previous, presentation.locale) : ''
    ])),
    margin: { left: 10, right: 10 },
    theme: 'plain',
    styles: {
      font: TURKISH_FONT,
      fontSize: 8,
      textColor: PDF_COLORS.text,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: PDF_COLORS.title,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { cellWidth: 120, halign: 'left' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 60, halign: 'right' },
      3: { cellWidth: 60, halign: 'right' }
    },
    didParseCell: (hook) => {
      if (hook.section !== 'body') return;
      const row = rows[hook.row.index];
      if (row.type === 'section') {
        hook.cell.styles.fillColor = PDF_COLORS.section;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.type === 'group') {
        hook.cell.styles.fillColor = PDF_COLORS.group;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.type === 'summary') {
        hook.cell.styles.fillColor = PDF_COLORS.summary;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.type === 'summary-strong') {
        hook.cell.styles.fillColor = PDF_COLORS.strong;
        hook.cell.styles.fontStyle = 'bold';
      } else if (row.type === 'total') {
        hook.cell.styles.fillColor = PDF_COLORS.total;
        hook.cell.styles.fontStyle = 'bold';
      }
    }
  });

  doc.save(filename);
}
